/**
 * Authentication context that provides global auth state.
 * This ensures all components share the same auth state instance.
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase, getSessionWithOrg } from '@/utils/supabase';

export interface AuthUser {
  id: string;
  email: string;
  orgId: string | null;
  roles: string[];
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let initialLoadComplete = false;
    
    console.log('AuthProvider: Initializing...');
    
    // Get initial session with error handling and timeout
    Promise.race([
      getSessionWithOrg(),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)), // 5s timeout
    ])
      .then((sessionData) => {
        if (!mounted) return;
        
        if (sessionData) {
          console.log('AuthProvider: Initial session found, user ID:', sessionData.session.user.id);
          setUser({
            id: sessionData.session.user.id,
            email: sessionData.session.user.email || '',
            orgId: sessionData.orgId || null,
            roles: sessionData.roles,
          });
          if (sessionData.orgId) {
            localStorage.setItem('org_id', sessionData.orgId);
          }
          localStorage.setItem('supabase.auth.token', sessionData.session.access_token);
        } else {
          console.log('AuthProvider: No initial session');
        }
        initialLoadComplete = true;
        setLoading(false);
      })
      .catch((error) => {
        console.error('AuthProvider: Error getting initial session:', error);
        if (mounted) {
          initialLoadComplete = true;
          setLoading(false);
        }
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('AuthProvider: Auth state change:', event, session ? 'has session' : 'no session');
      
      if (session) {
        // Always set user immediately from session, then try to get org context
        // This ensures isAuthenticated becomes true immediately
        const userFromSession: AuthUser = {
          id: session.user.id,
          email: session.user.email || '',
          orgId: null,
          roles: [],
        };
        
        console.log('AuthProvider: Processing session, user ID:', session.user.id);
        console.log('AuthProvider: Setting user state immediately from session');
        setUser(userFromSession);
        localStorage.setItem('supabase.auth.token', session.access_token);
        
        // Set loading to false immediately so ProtectedRoute can check auth state
        // Don't wait for org context - that can happen async
        setLoading(false);
        if (!initialLoadComplete) {
          initialLoadComplete = true;
        }
        
        // Now try to get org context (non-blocking)
        try {
          const sessionData = await getSessionWithOrg();
          if (sessionData && mounted) {
            console.log('AuthProvider: Got org context, orgId:', sessionData.orgId);
            // Update user with org context
            setUser({
              ...userFromSession,
              orgId: sessionData.orgId || null,
              roles: sessionData.roles,
            });
            if (sessionData.orgId) {
              localStorage.setItem('org_id', sessionData.orgId);
            }
            console.log('AuthProvider: User state updated with org context');
          } else if (!sessionData) {
            console.warn('AuthProvider: getSessionWithOrg returned null, user set without org context');
          }
        } catch (error) {
          console.error('AuthProvider: Error getting org context (non-fatal):', error);
          // User is already set from session, so this is fine
        }
      } else {
        console.log('AuthProvider: No session, clearing user state');
        setUser(null);
        localStorage.removeItem('org_id');
        localStorage.removeItem('supabase.auth.token');
        queryClient.clear();
        setLoading(false);
      }
      console.log('AuthProvider: Auth state change handler completed');
    });

    return () => {
      console.log('AuthProvider: Cleaning up...');
      mounted = false;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    
    // Wait for auth state change to propagate
    // The onAuthStateChange listener will update user state
    await new Promise((resolve) => setTimeout(resolve, 100));
    
    // Verify session was established
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Failed to establish session');
    }
  };

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) throw error;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    localStorage.removeItem('org_id');
    localStorage.removeItem('supabase.auth.token');
    queryClient.clear();
  };

  const value: AuthContextValue = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context.
 * Must be used within AuthProvider.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

